import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Notification } from 'src/app/core/models/notification-pg';
import { SecureUtils } from '../../../utils/secure-utils';
import { jsonParse } from '../../../utils/utils';
import { AuthMirrorService } from '../auth/auth-mirror.service';
import { Auth } from '../auth/auth.actions';
import { LogService } from '../log.service';
import { StoreService } from './store.service';

export enum EncriptedDataKeys {
  AUTH__STATE,
  TECUIDAMOS__STATE,
  // FAVORITES
  FAV_MC,
  FAV_OFFICES,
  FAV_GARAGES,
  FAV_DISCOUNTS,
  // SAVE
  SAVED__CAR_SINISTER_REPORT
}

enum PlainDataKeys {
  AUTH__APP_PREFERENCES,
  CACHE__PRODUCTS,
  CACHE__SPECIALITIES,
  CACHE__SUBSPECIALITIES,
  CACHE__SPECIALITIES_UPDATE_DATE,
  CACHE__SUBSPECIALITIES_UPDATE_DATE,
  CACHE__PRODUCT_UPDATE_DATE,
  CACHE__LAST_LOGIN_STATUS,
  updateVersionKey,
  tcCardImagesDownloaded,
  FIRST_APP_RUN,
  FIRST_ON_BOARDING_RUN
}

// Dont use them, deprecated
enum DeprecatedKeys {
  PG_NOTIFICATIONS,
  ENCRYPT_DATA,
  PIN,
  DOCUMENT,
  USER,
  REFRESHTOKEN,
  mets_a,
  mets_b,
  mets_c,
  mets_d,
  mets_e,
  mets_f,
  mets_g
}

@Injectable({
  providedIn: 'root'
})
export class SecureStoreService {
  private APP_SECRET = null;
  private readonly WEB_SECRET =
    '-C7u=+:wpI-hA-=Lg#_|SX7w,rCZ@Whw/jl(E7>s:`S&~l=S98ufd)lmX><00+Y';

  constructor(
    private store: StoreService,
    private platform: Platform,
    private authMirror: AuthMirrorService,
    private log: LogService
  ) {}

  public getNotification(key: string) {
    return this.getNotSecure<Notification>(key as any);
  }

  public saveNotification(key: string, data: Notification) {
    return this.saveNotSecure(key as any, data);
  }

  public deleteNotification(key: string) {
    return this.store.delete(key);
  }

  public async get<T>(key: keyof typeof EncriptedDataKeys) {
    if (!this.isSecretAvailable()) { return null; }

    const secret = await this.getSecret();
    const encriptedData = await this.store.get(key);
    const decriptedData = SecureUtils.decryptWithKey(encriptedData, secret);
    return jsonParse(decriptedData) as T;
  }

  public async getNotSecure<T>(key: keyof typeof PlainDataKeys) {
    const encriptedData = await this.store.get(key);
    const decriptedData = SecureUtils.decryptWithKey(
      encriptedData,
      this.WEB_SECRET
    );
    return jsonParse(decriptedData) as T;
  }

  public async save(key: keyof typeof EncriptedDataKeys, data: any) {
    if (!this.isSecretAvailable()) { return; }

    const secret = await this.getSecret();
    const encriptedData = SecureUtils.encryptWithKey(data, secret);
    return this.store.save(key, encriptedData);
  }

  public saveNotSecure(key: keyof typeof PlainDataKeys, data: any) {
    // Encrypted at least with web secret
    const encriptedData = SecureUtils.encryptWithKey(data, this.WEB_SECRET);
    return this.store.save(key, encriptedData);
  }

  public delete(
    key: keyof typeof EncriptedDataKeys | keyof typeof PlainDataKeys
  ) {
    return this.store.delete(key);
  }

  public deleteNotSecure(key: keyof typeof EncriptedDataKeys | keyof typeof PlainDataKeys) {
    return this.delete(key);
  }

  public deleteAllEncripted() {
    Object.keys(EncriptedDataKeys).forEach(key => this.store.delete(key));
  }

  public updateTokenPass(str: string) {
    this.APP_SECRET = str;
  }

  private async getSecret() {
    return this.platform.is('cordova')
      ? SecureUtils.getHashFrom(this.APP_SECRET)
      : this.WEB_SECRET;
  }

  private isSecretAvailable() {
    if (!this.platform.is('cordova')) {
      return true;
    }
    // User not logged yet, we cannot save/get in a secure way
    const { preferences }: Auth = this.authMirror.authService.getState();
    if (preferences) {
      return true;
    } else {
      this.log.d(this, 'Unable to retrieve secret');
      return false;
    }
  }

  /** @deprecated TODO Delete when old METS is not longer in production
   * This will delete old unused table keys from old METS
   */
  public deleteLegacyKeys() {
    Object.keys(DeprecatedKeys).forEach(key => this.store.delete(key));
  }
}
